import { z } from 'zod';

export const SCAN_EFFORT_LEVELS = ['HIGH', 'EXTRA_HIGH', 'EXTREME'] as const;

export const ScanEffortLevelSchema = z.enum(SCAN_EFFORT_LEVELS);

export type ScanEffortLevel = z.infer<typeof ScanEffortLevelSchema>;

export const DEFAULT_SCAN_EFFORT_LEVEL: ScanEffortLevel = 'HIGH';

export const SCAN_EFFORT_ITERATIONS: Record<ScanEffortLevel, number> = {
  HIGH: 1,
  EXTRA_HIGH: 3,
  EXTREME: 5,
};

export const ScanEffortSourceSchema = z.enum([
  'default',
  'application',
  'tenant',
]);

export type ScanEffortSource = z.infer<typeof ScanEffortSourceSchema>;

export const EffectiveScanEffortSchema = z.object({
  level: ScanEffortLevelSchema,
  iterations: z.number().int().positive(),
  source: ScanEffortSourceSchema,
});

export type EffectiveScanEffort = z.infer<typeof EffectiveScanEffortSchema>;

export const ApplicationScanEffortSchema = z.object({
  enabled: z.boolean(),
  applicationLevel: ScanEffortLevelSchema,
  tenantOverride: ScanEffortLevelSchema.nullable(),
  effective: EffectiveScanEffortSchema,
});

export type ApplicationScanEffort = z.infer<typeof ApplicationScanEffortSchema>;

export const UpdateApplicationScanEffortBodySchema = z.object({
  level: ScanEffortLevelSchema,
});

export type UpdateApplicationScanEffortBody = z.infer<
  typeof UpdateApplicationScanEffortBodySchema
>;

export const TenantScanEffortOverrideSchema = z.object({
  enabled: z.boolean(),
  tenantOverride: ScanEffortLevelSchema.nullable(),
});

export type TenantScanEffortOverride = z.infer<
  typeof TenantScanEffortOverrideSchema
>;

export const UpdateTenantScanEffortOverrideBodySchema = z.object({
  tenantOverride: ScanEffortLevelSchema.nullable(),
});

export type UpdateTenantScanEffortOverrideBody = z.infer<
  typeof UpdateTenantScanEffortOverrideBodySchema
>;

export const resolveEffectiveScanEffort = ({
  enabled,
  applicationLevel,
  tenantOverride,
}: {
  enabled: boolean;
  applicationLevel: ScanEffortLevel | null | undefined;
  tenantOverride: ScanEffortLevel | null | undefined;
}): EffectiveScanEffort => {
  if (!enabled) {
    return {
      level: DEFAULT_SCAN_EFFORT_LEVEL,
      iterations: SCAN_EFFORT_ITERATIONS[DEFAULT_SCAN_EFFORT_LEVEL],
      source: 'default',
    };
  }

  if (tenantOverride) {
    return {
      level: tenantOverride,
      iterations: SCAN_EFFORT_ITERATIONS[tenantOverride],
      source: 'tenant',
    };
  }

  if (applicationLevel) {
    return {
      level: applicationLevel,
      iterations: SCAN_EFFORT_ITERATIONS[applicationLevel],
      source: 'application',
    };
  }

  return {
    level: DEFAULT_SCAN_EFFORT_LEVEL,
    iterations: SCAN_EFFORT_ITERATIONS[DEFAULT_SCAN_EFFORT_LEVEL],
    source: 'default',
  };
};

export const scaleLimitByScanEffort = ({
  limit,
  iterations,
  ceiling,
}: {
  limit: number;
  iterations: number | null | undefined;
  ceiling: number;
}): number => {
  if (iterations == null || iterations <= 1) {
    return limit;
  }

  return Math.min(Math.max(limit, ceiling), Math.round(limit * iterations));
};
